import { t } from '@/i18n';

import { appShell } from './app';
import { isShareId, takeAppShare, type SharedContent } from './appShare';
import { MAX_FILE_BYTES, MAX_TEXT_BYTES } from './format';
import type { Shortcut } from './macKeys';

/**
 * The Mac's quick-send shortcut (apps/apple/Bridge/QuickSend.swift). Wherever the person is, the shortcut sends the
 * selected text, the copied text or a file picked in Finder: the shell reads it, leaves it in a note on
 * `window.bridgeApp.quickSend` and fires this event; the page seals it here, on this device, with the same code as
 * the Create button, and tells the shell the link, which the shell puts on the clipboard. The secret never goes
 * anywhere the Share menu's does not, and the page never logs it.
 */
export const QUICK_SEND_EVENT = 'bridge:quicksend';

/** The message handler the shell adds to the web view (settings, and the answer to each request). */
const HANDLER = 'bridgeQuickSend';

/** What a quick send makes: the defaults of the Create button — one day, one view. */
export const QUICK_TTL_SECONDS = 86_400;
export const QUICK_VIEWS = 1;

/** What the second shortcut makes: a private chat for two devices, open for a day. */
export const CHAT_TTL_SECONDS = 86_400;
export const CHAT_VIEWS = 2;

export type QuickSendRequest =
  | {
      readonly kind: 'send';
      readonly request: string;
      /** Null when the note held nothing the page can use. */
      readonly content: SharedContent | null;
    }
  | { readonly kind: 'chat'; readonly request: string };

/** Takes the request the shell left, once. Null in a browser and when there is none. */
export async function takeQuickSend(): Promise<QuickSendRequest | null> {
  if (appShell() === null) return null;
  const note = (window.bridgeApp as Record<string, unknown>).quickSend;
  const fields = typeof note === 'object' && note !== null ? (note as Record<string, unknown>) : null;
  const request = fields?.request;
  if (!isShareId(request)) return null;
  if (fields?.kind === 'chat') {
    Reflect.deleteProperty(window.bridgeApp as Record<string, unknown>, 'quickSend');
    return { kind: 'chat', request };
  }
  const content = await takeAppShare();
  // a note with a request and nothing to send is answered once, not on every look
  if (content === null) Reflect.deleteProperty(window.bridgeApp as Record<string, unknown>, 'quickSend');
  return { kind: 'send', request, content };
}

/** Why a request cannot be sealed, in words for the person. */
export function quickSendProblem(content: SharedContent): string | null {
  if (content.text !== null && new TextEncoder().encode(content.text).length > MAX_TEXT_BYTES) {
    return t('quickSend.textTooLong', { kb: MAX_TEXT_BYTES / 1024 });
  }
  if (content.file !== null && content.file.size > MAX_FILE_BYTES) {
    return t('quickSend.fileTooLarge', { mb: MAX_FILE_BYTES / 1024 / 1024 });
  }
  return null;
}

/** One of the two shortcuts: it sends a secret, or makes a private chat. */
export type ShortcutTarget = 'send' | 'chat';

export interface ShortcutSetting {
  readonly enabled: boolean;
  readonly shortcut: Shortcut;
}

export interface QuickSendState extends ShortcutSetting {
  /** The chat shortcut; `enabled` and `shortcut` above are the send shortcut's. */
  readonly chat: ShortcutSetting;
  /** Bridge may press ⌘C in the front app (System Settings → Privacy & Security → Accessibility). */
  readonly accessibility: boolean;
  /** This build can press ⌘C at all (the website build); the Mac App Store build only sends the clipboard. */
  readonly copiesSelection: boolean;
  readonly login: 'on' | 'off' | 'approval';
  /** The updater is part of this build (the website build): the settings then offer «Check for updates». */
  readonly updates: boolean;
  /** Why the last change did not take (the shortcut belongs to another app, say). */
  readonly problem: string | null;
  /** Which shortcut the problem is about. */
  readonly problemTarget: ShortcutTarget;
}

interface Handler {
  postMessage: (message: unknown) => Promise<unknown>;
}

function handler(): Handler | null {
  if (appShell()?.platform !== 'macos') return null;
  const webkit = (window as { webkit?: { messageHandlers?: Record<string, Handler | undefined> } }).webkit;
  return webkit?.messageHandlers?.[HANDLER] ?? null;
}

/** True where the shell offers the shortcut: the Mac app. */
export function quickSendAvailable(): boolean {
  return handler() !== null;
}

function parseState(value: unknown): QuickSendState | null {
  if (typeof value !== 'object' || value === null) return null;
  const { enabled, keyCode, modifiers, label, chat, accessibility, copiesSelection, login, updates, problem, problemTarget } = value as Record<string, unknown>;
  if (typeof enabled !== 'boolean' || typeof keyCode !== 'number' || typeof modifiers !== 'number' || typeof label !== 'string') return null;
  const second = typeof chat === 'object' && chat !== null ? (chat as Record<string, unknown>) : null;
  if (typeof second?.enabled !== 'boolean' || typeof second.keyCode !== 'number' || typeof second.modifiers !== 'number' || typeof second.label !== 'string') return null;
  return {
    enabled,
    shortcut: { keyCode, modifiers, label },
    chat: { enabled: second.enabled, shortcut: { keyCode: second.keyCode, modifiers: second.modifiers, label: second.label } },
    accessibility: accessibility === true,
    copiesSelection: copiesSelection !== false,
    login: login === 'on' || login === 'approval' ? login : 'off',
    updates: updates === true,
    problem: typeof problem === 'string' ? problem : null,
    problemTarget: problemTarget === 'chat' ? 'chat' : 'send',
  };
}

export type QuickSendCommand =
  | { readonly action: 'state' }
  | { readonly action: 'set'; readonly target: ShortcutTarget; readonly enabled: boolean; readonly shortcut: Shortcut }
  | { readonly action: 'accessibility' }
  | { readonly action: 'recording'; readonly on: boolean }
  | { readonly action: 'login'; readonly on: boolean }
  | { readonly action: 'checkUpdates' };

/** Asks the shell to do something with the shortcut and answers with how things stand after it. */
export async function askShell(command: QuickSendCommand): Promise<QuickSendState | null> {
  const shell = handler();
  if (shell === null) return null;
  const message =
    command.action === 'set' ? { action: 'set', target: command.target, enabled: command.enabled, ...command.shortcut } : command;
  try {
    return parseState(await shell.postMessage(message));
  } catch {
    return null;
  }
}

/** Tells the shell how a request ended: the link to put on the clipboard, or why there is none. */
export function reportQuickSend(request: string, outcome: { readonly link: string } | { readonly problem: string }): void {
  void handler()?.postMessage({ action: 'finished', request, ...outcome }).catch(() => undefined);
}
